<template>
    <section class="document-research-source" :data-process-id="processId">
        <div class="document-research-source__header">
            <span class="documents-and-webpages-step__file-icon" aria-hidden="true">
                <FeatherIcon icon="LinkIcon" size="20" />
            </span>

            <div class="document-research-source__copy">
                <a
                    class="documents-and-webpages-step__file-name"
                    :href="link.url"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ link.url }}
                </a>
                <small v-if="candidateCount > 0" class="text-less-muted">
                    {{ candidateCountLabel }}
                </small>
                <small v-if="failureMessage" class="text-danger">
                    {{ failureMessage }}
                </small>
            </div>

            <BBadge
                v-if="!canReview"
                :variant="statusVariant"
                class="document-research-source__status"
            >
                <BSpinner v-if="isProcessing" small class="mr-50" />
                {{ statusLabel }}
            </BBadge>

            <BButton
                v-if="canReview"
                variant="primary"
                :disabled="disabled"
                @click="$emit('review', process)"
            >
                {{ translate('Component(Button):review', 'Review') }}
            </BButton>

            <BButton
                v-if="candidates.length || canFinish"
                variant="flat-secondary"
                size="sm"
                class="document-research-source__toggle btn-icon"
                :aria-expanded="expanded ? 'true' : 'false'"
                @click="expanded = !expanded"
            >
                <FeatherIcon :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="18" />
                <span class="sr-only">
                    {{ expanded
                        ? translate('Component(Button):collapse', 'Collapse')
                        : translate('Component(Button):expand', 'Expand') }}
                </span>
            </BButton>
        </div>

        <div
            v-if="expanded && (candidates.length || canFinish)"
            class="document-research-source__body"
        >
            <div
                v-for="candidate in visibleCandidates"
                :key="candidate.id"
                class="document-research-source__candidate"
                @click="toggleCandidate(candidate)"
            >
                <BFormCheckbox
                    v-if="canSelect"
                    v-model="selectedCandidateIds"
                    :value="candidate.id"
                    :disabled="disabled"
                    class="document-research-source__checkbox"
                    @click.native.stop
                />
                <FeatherIcon
                    v-else
                    icon="FileTextIcon"
                    size="18"
                    class="document-research-source__candidate-icon"
                    aria-hidden="true"
                />

                <div class="document-research-source__candidate-copy">
                    <strong>{{ candidate.filename }}</strong>
                    <small class="text-less-muted">
                        {{ candidateMetadata(candidate) }}
                    </small>
                    <div v-if="candidate.regulationScores && candidate.regulationScores.length">
                        <BBadge
                            v-for="score in candidate.regulationScores"
                            :key="`${candidate.id}-${score.regulation}`"
                            variant="light-secondary"
                            class="mr-50 mt-25"
                        >
                            {{ regulationScoreLabel(score) }}
                        </BBadge>
                    </div>
                </div>

                <BButton
                    variant="outline-secondary"
                    size="sm"
                    :disabled="disabled || previewingCandidateId === candidate.id"
                    @click.stop="preview(candidate)"
                >
                    <BSpinner
                        v-if="previewingCandidateId === candidate.id"
                        small
                        class="mr-50"
                    />
                    {{ translate('Component(Button):preview', 'Preview') }}
                </BButton>
            </div>

            <BAlert v-if="actionError" show variant="danger" class="mt-1 mb-0">
                <div class="alert-body">{{ actionError }}</div>
            </BAlert>

            <div v-if="canSelect || canFinish" class="document-research-source__footer">
                <span v-if="canSelect" class="text-less-muted">
                    {{ selectionCountLabel }}
                </span>
                <div class="document-research-source__actions">
                    <BButton
                        v-if="canFinish"
                        variant="outline-secondary"
                        :disabled="disabled"
                        @click="finishWithoutDocuments"
                    >
                        {{ translate(
                            'Page(ReceivedRequest):Component(DocumentsAndWebpages):none-relevant',
                            'None are relevant',
                        ) }}
                    </BButton>
                    <BButton
                        v-if="canSelect"
                        variant="primary"
                        :disabled="disabled || selectedCandidateIds.length === 0"
                        @click="submitSelection"
                    >
                        {{ analyzeLabel }}
                    </BButton>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BFormCheckbox,
    BSpinner,
} from 'bootstrap-vue'
import {
    DocumentResearchStatus,
    isActiveDocumentResearchStatus,
} from './document-research'

const STATUS_FALLBACKS = Object.freeze({
    [DocumentResearchStatus.CREATED]: 'Starting document search',
    [DocumentResearchStatus.DISCOVERY_DISPATCH_PENDING]: 'Starting document search',
    [DocumentResearchStatus.DISCOVERY_PENDING]: 'Searching for documents',
    [DocumentResearchStatus.DISCOVERY_REVIEW_REQUIRED]: 'Action required',
    [DocumentResearchStatus.DISCOVERY_FAILED]: 'Document search failed',
    [DocumentResearchStatus.AWAITING_DOCUMENT_SELECTION]: 'Selection required',
    [DocumentResearchStatus.MATERIALIZATION_PENDING]: 'Preparing documents',
    [DocumentResearchStatus.ANALYSIS_DISPATCH_PENDING]: 'Starting document analysis',
    [DocumentResearchStatus.ANALYSIS_PENDING]: 'Analyzing documents',
    [DocumentResearchStatus.REVIEW_DISPATCH_PENDING]: 'Preparing review',
    [DocumentResearchStatus.REVIEW_PENDING]: 'Ready for review',
    [DocumentResearchStatus.COMPLETED]: 'Completed',
    [DocumentResearchStatus.COMPLETED_EMPTY]: 'No relevant documents',
    [DocumentResearchStatus.REJECTED]: 'Rejected',
    [DocumentResearchStatus.FAILED]: 'Failed',
    [DocumentResearchStatus.UNAVAILABLE]: 'Unavailable',
})

const maximumRegulationConfidence = candidate => {
    const scores = Array.isArray(candidate.regulationScores)
        ? candidate.regulationScores
            .map(score => score && score.score)
            .filter(score => score !== null && score !== '' && Number.isFinite(Number(score)))
            .map(score => Number(score))
        : []

    return scores.length > 0 ? Math.max(...scores) : null
}

export default {
    name: 'DocumentResearchSource',
    components: {
        BAlert,
        BBadge,
        BButton,
        BFormCheckbox,
        BSpinner,
    },
    props: {
        link: { type: Object, required: true },
        process: { type: Object, default: null },
        disabled: { type: Boolean, default: false },
        previewingCandidateId: { type: [String, Number], default: null },
    },
    data() {
        return {
            expanded: this.process && [
                DocumentResearchStatus.AWAITING_DOCUMENT_SELECTION,
                DocumentResearchStatus.DISCOVERY_REVIEW_REQUIRED,
            ].includes(this.process.status),
            selectedCandidateIds: [],
            actionError: null,
        }
    },
    computed: {
        processId() {
            return this.link.documentResearch && this.link.documentResearch.processId
                ? String(this.link.documentResearch.processId)
                : null
        },
        status() {
            return this.process && this.process.status
                ? this.process.status
                : null
        },
        details() {
            return this.process && this.process.details ? this.process.details : null
        },
        candidates() {
            return this.details && Array.isArray(this.details.candidates)
                ? this.details.candidates
                : []
        },
        sortedCandidates() {
            return this.candidates
                .map((candidate, index) => ({
                    candidate,
                    confidence: maximumRegulationConfidence(candidate),
                    index,
                }))
                .sort((left, right) => {
                    if (left.confidence === right.confidence) return left.index - right.index
                    if (left.confidence === null) return 1
                    if (right.confidence === null) return -1
                    return right.confidence - left.confidence
                })
                .map(({ candidate }) => candidate)
        },
        visibleCandidates() {
            if (this.canSelect) {
                return this.sortedCandidates
            }

            const selected = this.sortedCandidates
                .filter(candidate => candidate.selectionStatus === 'selected')
            return selected.length > 0 ? selected : this.sortedCandidates
        },
        candidateSignature() {
            return this.candidates
                .map(candidate => `${candidate.id}:${candidate.selectionStatus || 'undecided'}`)
                .join('|')
        },
        candidateCount() {
            return Number(this.process && this.process.candidateCount) || this.candidates.length
        },
        allowedActions() {
            return this.details && Array.isArray(this.details.allowedActions)
                ? this.details.allowedActions
                : []
        },
        canSelect() {
            return this.status === DocumentResearchStatus.AWAITING_DOCUMENT_SELECTION
                && this.allowedActions.includes('select_documents')
        },
        canFinish() {
            return this.allowedActions.includes('finish_without_documents')
        },
        canReview() {
            return this.status === DocumentResearchStatus.REVIEW_PENDING
                && Boolean(this.process && this.process.reviewProcessId)
        },
        isProcessing() {
            return isActiveDocumentResearchStatus(this.status)
                && this.status !== DocumentResearchStatus.REVIEW_PENDING
        },
        statusLabel() {
            if (!this.processId) {
                return this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):link-saved',
                    'Saved',
                )
            }

            const status = this.status || DocumentResearchStatus.UNAVAILABLE
            return this.translate(
                `Page(ReceivedRequest):Component(DocumentResearchStatus):${status.replace(/_/g, '-')}`,
                STATUS_FALLBACKS[status] || 'Processing status unavailable',
            )
        },
        statusVariant() {
            if (!this.processId) return 'light-success'
            if ([DocumentResearchStatus.COMPLETED].includes(this.status)) return 'light-success'
            if ([DocumentResearchStatus.REJECTED, DocumentResearchStatus.FAILED, DocumentResearchStatus.DISCOVERY_FAILED].includes(this.status)) return 'light-danger'
            if ([DocumentResearchStatus.AWAITING_DOCUMENT_SELECTION, DocumentResearchStatus.DISCOVERY_REVIEW_REQUIRED].includes(this.status)) return 'light-warning'
            if (this.status === DocumentResearchStatus.REVIEW_PENDING) return 'light-primary'
            return 'light-secondary'
        },
        failureMessage() {
            const failure = this.process && this.process.failure
            return failure && failure.message ? failure.message : null
        },
        candidateCountLabel() {
            return this.translate(
                'Page(ReceivedRequest):Component(DocumentsAndWebpages):documents-found',
                `${this.candidateCount} document(s) found`,
                { count: this.candidateCount },
            )
        },
        selectionCountLabel() {
            return this.translate(
                'Page(ReceivedRequest):Component(DocumentsAndWebpages):documents-selected',
                `${this.selectedCandidateIds.length} document(s) selected`,
                { count: this.selectedCandidateIds.length },
            )
        },
        analyzeLabel() {
            return this.translate(
                'Page(ReceivedRequest):Component(DocumentsAndWebpages):analyze-selected',
                `Analyze ${this.selectedCandidateIds.length} selected`,
                { count: this.selectedCandidateIds.length },
            )
        },
    },
    watch: {
        candidateSignature: {
            immediate: true,
            handler() {
                this.selectedCandidateIds = this.candidates
                    .filter(candidate => candidate.selectionStatus === 'selected')
                    .map(candidate => candidate.id)
            },
        },
        status(status) {
            if ([
                DocumentResearchStatus.AWAITING_DOCUMENT_SELECTION,
                DocumentResearchStatus.DISCOVERY_REVIEW_REQUIRED,
            ].includes(status)) {
                this.expanded = true
            }
        },
    },
    methods: {
        translate(key, fallback, params = {}) {
            const translation = this.$t(key, params)
            return translation && translation !== key ? translation : fallback
        },
        candidateMetadata(candidate) {
            const values = []
            if (candidate.contentType) values.push(candidate.contentType)
            if (Number(candidate.sizeBytes) > 0) {
                values.push(`${Math.ceil(candidate.sizeBytes / 1024)} KB`)
            }
            return values.join(' · ')
        },
        regulationScoreLabel(score) {
            const percentage = Math.round(Number(score.score) * 100)
            return `${String(score.regulation || '').toUpperCase()} ${percentage}%`
        },
        toggleCandidate(candidate) {
            if (!this.canSelect || this.disabled) return
            const index = this.selectedCandidateIds.indexOf(candidate.id)
            if (index === -1) {
                this.selectedCandidateIds.push(candidate.id)
            } else {
                this.selectedCandidateIds.splice(index, 1)
            }
        },
        preview(candidate) {
            this.actionError = null
            this.$emit('preview', { processId: this.processId, candidate })
        },
        async confirm(message, title, okTitle, okVariant = 'primary') {
            if (!this.$bvModal || typeof this.$bvModal.msgBoxConfirm !== 'function') {
                return true
            }
            return this.$bvModal.msgBoxConfirm(message, {
                title,
                okTitle,
                cancelTitle: this.translate('Component(Button):cancel', 'Cancel'),
                okVariant,
                cancelVariant: 'outline-secondary',
                centered: true,
            })
        },
        async submitSelection() {
            if (!this.canSelect || this.selectedCandidateIds.length === 0 || this.disabled) return
            const confirmed = await this.confirm(
                this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):selection-final-confirmation',
                    'This selection cannot be changed after analysis starts. Continue?',
                ),
                this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):analyze-documents-title',
                    'Analyze selected documents?',
                ),
                this.translate('Component(Button):continue', 'Continue'),
            )
            if (!confirmed) return

            this.actionError = null
            this.$emit('select', {
                processId: this.processId,
                candidateIds: [...this.selectedCandidateIds],
                failureCallback: this.handleActionFailure,
            })
        },
        async finishWithoutDocuments() {
            if (!this.canFinish || this.disabled) return
            const confirmed = await this.confirm(
                this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):finish-empty-confirmation',
                    'Finish this document search without analyzing any documents?',
                ),
                this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):finish-empty-title',
                    'No relevant documents?',
                ),
                this.translate('Component(Button):continue', 'Continue'),
                'danger',
            )
            if (!confirmed) return

            this.actionError = null
            this.$emit('finish-without-documents', {
                processId: this.processId,
                failureCallback: this.handleActionFailure,
            })
        },
        handleActionFailure(error) {
            const data = error && error.response ? error.response.data : null
            this.actionError = (data && (data.detail || data['hydra:description']))
                || (error && error.message)
                || this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):research-action-failed',
                    'The action could not be completed.',
                )
        },
    },
}
</script>

<style lang="scss" scoped>
.document-research-source {
    border: 1px solid #ebe9f1;
    border-radius: .357rem;

    & + & {
        margin-top: .75rem;
    }
}

.document-research-source__header {
    display: flex;
    min-height: 4rem;
    padding: .85rem 1rem;
    align-items: center;
}

.documents-and-webpages-step__file-icon {
    display: inline-flex;
    flex: 0 0 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 1rem;
    border: 1px solid rgba(245, 128, 73, .55);
    border-radius: .357rem;
    align-items: center;
    justify-content: center;
    color: #f58049;
}

.documents-and-webpages-step__file-name {
    max-width: 100%;
    overflow: hidden;
    color: #2f2c52;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.document-research-source__copy,
.document-research-source__candidate-copy {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
}

.document-research-source__status {
    flex: 0 0 auto;
    margin: 0 1rem;
}

.document-research-source__toggle {
    flex: 0 0 auto;
    margin-left: .25rem;
}

.document-research-source__body {
    padding: 0 1rem 1rem 4.5rem;
    border-top: 1px solid #ebe9f1;
}

.document-research-source__candidate {
    display: flex;
    padding: .85rem 0;
    align-items: center;
    gap: .75rem;

    & + & {
        border-top: 1px solid #ebe9f1;
    }
}

.document-research-source__checkbox,
.document-research-source__candidate-icon {
    flex: 0 0 auto;
}

.document-research-source__footer {
    display: flex;
    padding-top: 1rem;
    border-top: 1px solid #ebe9f1;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.document-research-source__actions {
    display: flex;
    gap: .75rem;
}

@media (max-width: 575.98px) {
    .document-research-source__header {
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .document-research-source__status {
        margin: .75rem .75rem 0 3.5rem;
    }

    .document-research-source__body {
        padding-left: 1rem;
    }

    .document-research-source__candidate,
    .document-research-source__footer,
    .document-research-source__actions {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
